import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-supabase-anon-key';

// Log configuration status (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('Supabase Configuration:');
  console.log('URL:', supabaseUrl === 'https://your-project.supabase.co' ? '⚠️ NOT CONFIGURED (using placeholder)' : '✅ Configured');
  console.log('Anon Key:', supabaseAnonKey === 'your-supabase-anon-key' ? '⚠️ NOT CONFIGURED (using placeholder)' : '✅ Configured');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Course interface matching your database schema
export interface Course {
  id: string;
  name: string;
  location: string;
  latitude: number;
  longitude: number;
  par3_holes: number;
  created_at: string;
  updated_at?: string;
  image_url?: string;
  holes?: Hole[];
}

export interface Hole {
  hole_number: number;
  total_shots?: number;
  hole_in_ones?: number;
  shots_since_last_hio?: number;
}

export interface Prize {
  title: string;
  value: number;
  description: string;
  image_url?: string;
}

export interface Sponsor {
  name: string;
  logo_url?: string;
  website_url?: string;
}


// Service for fetching courses
export class CourseService {
  static async getCourses(): Promise<Course[]> {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          holes:holes(
            hole_number,
            total_shots,
            hole_in_ones,
            shots_since_last_hio
          )
        `)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching courses:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('Courses fetched successfully:', data?.length || 0, 'courses');
      return data || [];
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      // Check if it's a configuration issue
      if (supabaseUrl === 'https://your-project.supabase.co' || supabaseAnonKey === 'your-supabase-anon-key') {
        console.error('⚠️ Supabase environment variables not configured! Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file');
      }
      return [];
    }
  }

  static async getCourseById(id: string): Promise<Course | null> {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select(`
          *,
          holes:holes(
            hole_number,
            total_shots,
            hole_in_ones,
            shots_since_last_hio
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching course:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch course:', error);
      return null;
    }
  }

  static async searchCourses(query: string): Promise<Course[]> {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .or(`name.ilike.%${query}%,location.ilike.%${query}%`)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error searching courses:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Failed to search courses:', error);
      return [];
    }
  }
}
